import {
    ContentAdapterEvents,
    IContentAdapter, IContentAdapterCasinoGameInfo,
    IContentAdapterCasinoGamesInfoResponse, IContentAdapterClientInfo, IContentAdapterElasticEventData,
    IContentAdapterEvents, IContentAdapterUserInfo, IDeepLink
} from "@sparkware/uc-sdk-core";
import {IServiceFactoryExtended} from "./services/service-factory-extended.interface";
import {Check} from "@sparkware/app-utils";
import {B2C_APP_ID} from "./orbit-api.interface";
import {UndefinedString} from "../common/types/nullable-types";

export const CONTENT_ADAPTER_OBJECT_NAME = 'contentAdapter';

export class B2CAdapter implements IContentAdapter {
    constructor(private readonly _services: IServiceFactoryExtended) {
        this.events = new ContentAdapterEvents(_services.messageBroker.channels)
    }

    readonly events: IContentAdapterEvents;

    async getCasinoGames(gameTypes: number[]): Promise<IContentAdapterCasinoGamesInfoResponse> {
        gameTypes = gameTypes || [];

        let foundGames = {};
        let missingGames = [];
        if (gameTypes.length === 0) {
            return {
                foundGames,
                missingGames,
            };
        }

        let gamesMap = this._services.games.gamesMap;
        if (Check.isEmpty(gamesMap)) {
            gamesMap = await this._services.games.getUnrestrictedGamesInfoMap(gameTypes);
        }

        gameTypes.forEach(gameType => {
            const gameInfo = gamesMap[gameType];
            if (gameInfo) {
                foundGames[gameType] = {
                    gameType: gameInfo.gameType,
                    title: gameInfo.title,
                    gameGroup: gameInfo.gameGroup,
                    gameImage: gameInfo.getGameImage(),
                    gameImageAlt: gameInfo.getImageSeoAlt()
                };
            } else {
                missingGames.push(gameType);
            }
        });

        return {
            foundGames,
            missingGames
        };
    }

    async getCasinoGamesWithoutBonus(): Promise<Record<number, IContentAdapterCasinoGameInfo>> {
        const gamesTypes = this._services.configuration.usedGameTypesBySite;

        if(gamesTypes?.length > 0){
            const gamesInfo = await this._services.sdk.getGamesInfo(gamesTypes);

            return Object.keys(gamesInfo)
                .filter(gameType => gamesInfo[gameType].allowWithdrawOnlyFromReal === true)
                .reduce((acc, gameType) => {
                    acc[gameType] = gamesInfo[gameType];
                    return acc;
                }, {});
        }

        return {};
    }

    async getClientInfo(): Promise<IContentAdapterClientInfo> {
        return {
            brandId: this._services.sdk.getBrandID(),
            subBrandId: this._services.sdk.getSubBrandID(),
            languageIso2: this._services.language.currentLanguageIso2,
            productPackageId: this._services.sdk.getProductPackageId(),
            systemId: this._services.sdk.getSkin().getPlatformSystemID(),
            features: {},
            cms: {
                footerComponentId: parseInt(this._services.configuration.data.footer.componentID),
            },
            device: {
                isHybrid: this._services.device.hybrid.isHybrid,
                isIOS: this._services.device.os.isIOS,
                isMobile: this._services.device.os.isMobileOS
            },
            environment: this._services.configuration.startupConfiguration.sdk.config.ExtraProperties.env
        };
    }

    

    async getUserInfo(): Promise<IContentAdapterUserInfo> {
        const userService = this._services.user;
        const isAuthorized = userService.isAuthorized;

        let abTestGroup: UndefinedString = undefined;
        if(this._services.sdk.getCmsUserSignature().message) {
            try {
                abTestGroup = JSON.parse(this._services.sdk.getCmsUserSignature().message).B2CABTest;
            } catch (err) {
                this._services.logger.error('Failed to get B2C A/B test group name', err);
            }

        }

        if(isAuthorized) {
            return {
                isAuthorized: isAuthorized,
                abTestGroup: abTestGroup,
                userData: {
                    isPromotionPermitted: userService.userData.IsPromotionPermitted,

                }
            }
        }

        return {
            isAuthorized: false,
            abTestGroup: abTestGroup
        };
    }


    async openDeepLink(deepLink: IDeepLink): Promise<void> {
        const deepLinkService = await this._services.deepLink;
        await deepLinkService.executeDeepLink(deepLink);
    }

    sendElasticInfoEvent(data: IContentAdapterElasticEventData): boolean {
        let {eventType, category, action, label, value, customData, sendToDWH, appId} = data;
        appId = appId ?? B2C_APP_ID;
        sendToDWH = sendToDWH ?? true;

        if (!Check.isString(eventType) ||
            (!Check.isNullOrUndefined(category) && !Check.isString(category)) ||
            (!Check.isNullOrUndefined(action) && !Check.isString(action)) ||
            (!Check.isNullOrUndefined(label) && !Check.isString(label))) {
            return false;
        }

        const MAX_FIELD_SIZE = 100;
        eventType = (eventType?.length > MAX_FIELD_SIZE) ? eventType.slice(0, MAX_FIELD_SIZE) : eventType;
        category = (category?.length > MAX_FIELD_SIZE) ? category.slice(0, MAX_FIELD_SIZE) : category;
        action = (action?.length > MAX_FIELD_SIZE) ? action.slice(0, MAX_FIELD_SIZE) : action;
        label = (label?.length > MAX_FIELD_SIZE) ? label.slice(0, MAX_FIELD_SIZE) : label;

        if (Check.isNullOrUndefined(customData)) {
            this._services.analytics.sendElasticInfoEvent({ eventType, category, action, label, value, customData, appID: appId }, sendToDWH);
            return true;
        }

        if (Check.isString(customData)) {
            customData = (customData.length > MAX_FIELD_SIZE) ? customData.slice(0, MAX_FIELD_SIZE) : customData;
            this._services.analytics.sendElasticInfoEvent({ eventType, category, action, label, value, customData: appId ? { [appId + "_data"]: customData } : customData, appID: appId, }, sendToDWH);
            return true;
        } else if (Check.isObject(customData)) {
            const MAX_FIELDS_COUNT = 10;
            let fieldsCount: number = 0;
            for (const prop in customData) {
                if (!Check.isString(customData[prop])) {
                    return false; //only allowing string values in the customData fields - dismissing the event otherwise
                }
                if (customData[prop].length > MAX_FIELD_SIZE) {
                    customData[prop] = customData[prop].slice(0, MAX_FIELD_SIZE);//truncate string to max 100 chars
                }
                fieldsCount++;
            }
            if (fieldsCount > MAX_FIELDS_COUNT) {
                return false; //too many custom fields, event dismissed
            }
            this._services.analytics.sendElasticInfoEvent({ eventType, category, action, label, value, customData: appId ? { [appId + "_data"]: customData } : customData, appID: appId}, sendToDWH);
            return true;
        }

        return false;
    }

    async isPwaInstallable(): Promise<boolean> {
        return (await this._services.progressiveWebApp).isPWAInstallable;
    }

    async showPwaPrompt(): Promise<void> {
        await (await this._services.progressiveWebApp).prompt();
    }
}